import {Modal, Form, Button, Space, Alert} from 'antd';
import {Form as AntForm} from 'antd';
import {UserSelectField} from 'components/userSelectField/UserSelectField';

/**
 * Hand a ticket off to another admin.
 *
 * The picker uses the existing UserSelectField with `admin` set, so it
 * lists only admin users. The field returns a semicolon-joined string
 * `name;email;id` — we split on it the same way AssignPrimaryAgentModal
 * does (the source of truth for that contract).
 *
 * Excludes the current admin from being picked (handing a ticket back to
 * yourself is a no-op and would be confusing in the audit trail). If the
 * select-field doesn't support an exclude list yet, we validate on submit
 * and surface the error instead of letting the call through.
 */
export const HandOverModal = ({
  isOpen,
  onClose,
  onHandOver,
  loading,
  // Who's *currently* assigned to the ticket. We block picking them again
  // (handing the ticket back to themselves is a no-op that just litters
  // the audit trail). null = no current assignee, no restriction.
  currentAssigneeId,
  currentAssigneeName,
  title = 'Hand Over Ticket',
  submitLabel = 'Hand Over',
}) => {
  const [form] = Form.useForm();
  const agentSelection = AntForm.useWatch('agent', form);

  const handleFinish = values => {
    // UserSelectField returns "name;email;id" — pluck the pieces we need.
    const parts = String(values.agent || '').split(';');
    const name = (parts[0] || '').trim();
    const id = (parts[2] || '').trim();
    if (!id) {
      form.setFields([
        {name: 'agent', errors: ['Please pick an admin from the list.']},
      ]);
      return;
    }
    if (currentAssigneeId && String(id) === String(currentAssigneeId)) {
      form.setFields([
        {
          name: 'agent',
          errors: [
            `Pick a different admin — the ticket is already assigned to ${
              currentAssigneeName || 'this admin'
            }.`,
          ],
        },
      ]);
      return;
    }
    onHandOver({id, name}, () => {
      form.resetFields();
      onClose();
    });
  };

  return (
    <Modal
      title={title}
      open={isOpen}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      footer={null}
      width={500}
      destroyOnClose>
      <Alert
        type="info"
        showIcon
        style={{marginBottom: 16}}
        message={
          currentAssigneeName
            ? `You'll release this ticket from ${currentAssigneeName} and assign it to the admin you pick.`
            : "You'll assign this ticket to the admin you pick."
        }
      />
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="agent"
          label="Assign to"
          rules={[{required: true, message: 'Please select an admin'}]}>
          <UserSelectField
            users={agentSelection}
            mode="single"
            setUsers={value => form.setFieldValue('agent', value)}
            placeholder="Search admins by name or email"
            admin
          />
        </Form.Item>
        <Form.Item style={{marginBottom: 0}}>
          <Space style={{width: '100%', justifyContent: 'flex-end'}}>
            <Button
              onClick={() => {
                onClose();
                form.resetFields();
              }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {submitLabel}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};
